import React, { useState, useEffect, useCallback } from 'react';
import { Clock, X, CheckCircle, ChevronRight, ChevronLeft, FileText, AlertCircle, Calendar, Timer } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { useToast } from '@/app/providers';
import { AmenityPricingMode } from '@yawi/types';
import type { AmenityAvailability } from '@yawi/types';
import { amenityRentalCost, resolveAmenityPricing } from '@yawi/domain';
import { Amenity, AmenityReservation } from '@/shared/types';
import { Button } from '../atoms/Button';
import { DatePicker } from '../atoms/DatePicker';
import { Select } from '../atoms/Select';
import { Checkbox } from '../atoms/Checkbox';
import { useServices } from '@/app/providers/ServiceContext';

// ─── Types ───────────────────────────────────────────────────────────────────

interface AmenityBookingModalProps {
  amenity: Amenity;
  onClose: () => void;
  onConfirm: (data: { amenityId: string; date: string; startTime: string; hours: number }) => Promise<void>;
  memberId: string;
  memberName: string;
  /** Sección de la unidad que reserva: define el precio si la amenidad tiene uno por sección. */
  sectionId?: string | null;
  /** Flujo del administrador: el cargo lo paga el titular de la unidad, no quien opera el wizard. Ajusta títulos, avisos y el botón final. */
  adminMode?: boolean;
}

type TimeSlot = {
  time: string;
  display: string;
  isOccupied: boolean;
  isPast: boolean;
  isValidStart: boolean;
  inSelection: boolean;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TOTAL_STEPS = 3;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toDateString = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const padHour = (h: number) => `${String(h).padStart(2, '0')}:00`;

// "Ahora" (fecha YYYY-MM-DD y hora) en la zona horaria de la amenidad. Debe coincidir
// con la validación del backend, que convierte usando la zona del amenity; usar la del
// navegador provoca falsos "debe ser futura" cuando difieren los husos.
const nowInTimezone = (timezone?: string | null): { date: string; hour: number } => {
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  };
  if (timezone) opts.timeZone = timezone;
  const parts = new Intl.DateTimeFormat('en-CA', opts).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const hour = get('hour') === '24' ? 0 : parseInt(get('hour'));
  return { date: `${get('year')}-${get('month')}-${get('day')}`, hour };
};

function buildOccupiedSlots(reservations: AmenityReservation[]): string[] {
  return reservations.flatMap((r) => {
    const start = parseInt(r.startTime.split(':')[0]);
    return Array.from({ length: r.hours }, (_, i) => padHour(start + i));
  });
}

function generateTimeSlots(
  hours: number,
  occupiedSlots: string[],
  selectedTime: string,
  currentHour: number,
  openHour: number,
  closeHour: number
): TimeSlot[] {
  const selectedStartInt = selectedTime ? parseInt(selectedTime.split(':')[0]) : -1;

  return Array.from({ length: closeHour - openHour }, (_, idx) => {
    const i = openHour + idx;
    const time = padHour(i);
    const isPast = currentHour !== -1 && i <= currentHour;
    const isOccupied = occupiedSlots.includes(time) || isPast;

    let isValidStart = !isOccupied;
    if (isValidStart) {
      if (i + hours > closeHour) {
        isValidStart = false;
      } else {
        for (let h = 1; h < hours; h++) {
          if (
            occupiedSlots.includes(padHour(i + h)) ||
            (currentHour !== -1 && i + h <= currentHour)
          ) {
            isValidStart = false;
            break;
          }
        }
      }
    }

    return {
      time,
      display: time,
      isOccupied,
      isPast,
      isValidStart,
      inSelection: !!selectedTime && i >= selectedStartInt && i < selectedStartInt + hours,
    };
  });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const StepIndicator: React.FC<{ step: number }> = ({ step }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: TOTAL_STEPS }, (_, i) => {
      const n = i + 1;
      const isActive = n === step;
      const isDone = n < step;
      return (
        <React.Fragment key={n}>
          <div
            className={`
              w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold
              transition-all duration-300
              ${isDone ? 'bg-emerald-500 text-white' : isActive ? 'bg-indigo-600 text-white ring-4 ring-indigo-100' : 'bg-slate-100 text-slate-400'}
            `}
          >
            {isDone ? '✓' : n}
          </div>
          {n < TOTAL_STEPS && (
            <div
              className={`h-0.5 w-6 rounded-full transition-all duration-500 ${isDone ? 'bg-emerald-400' : 'bg-slate-100'}`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const SummaryRow: React.FC<{ label: React.ReactNode; value: React.ReactNode; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <div className={`flex justify-between items-center py-2 ${highlight ? 'border-t border-slate-200 mt-1 pt-3' : ''}`}>
    <span className={`text-sm ${highlight ? 'font-bold text-slate-800' : 'text-slate-500'}`}>{label}</span>
    <span className={`font-mono ${highlight ? 'text-2xl font-bold text-indigo-600' : 'text-sm font-medium text-slate-700'}`}>
      {value}
    </span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const AmenityBookingModal: React.FC<AmenityBookingModalProps> = ({
  amenity,
  onClose,
  onConfirm,
  memberId,
  memberName,
  sectionId,
  adminMode = false,
}) => {
  const { t } = useI18n();
  const toast = useToast();
  const { amenityReservationRepo, amenityRepo } = useServices();

  const today = toDateString(new Date());

  // ─── State ────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(today);
  const [selectedTime, setSelectedTime] = useState('');
  const [hours, setHours] = useState(amenity.minHours || 1);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [occupiedSlots, setOccupiedSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availability, setAvailability] = useState<AmenityAvailability | null>(null);

  // ─── Derived ──────────────────────────────────────────────────────────────
  const openHour = availability?.openTime ? parseInt(availability.openTime.split(':')[0]) : 8;
  const closeHour = availability?.closeTime ? parseInt(availability.closeTime.split(':')[0]) : 22;
  const minH = availability?.minHours ?? amenity.minHours ?? 1;
  const maxH = availability?.maxHours ?? amenity.maxHours ?? 4;

  const pricing = resolveAmenityPricing(amenity, sectionId);
  const rentalCost = amenityRentalCost(pricing, hours);
  const depositAmount = pricing.depositAmount;
  const totalCost = rentalCost + depositAmount;

  const endTime = selectedTime ? padHour(parseInt(selectedTime.split(':')[0]) + hours) : '';
  // Calcular "hoy" y la hora actual en la zona de la amenidad para que el grid de horas
  // coincida con la validación del backend (evita falsos "la fecha debe ser futura").
  const now = nowInTimezone(availability?.timezone);
  const isToday = date === now.date;
  const currentHour = isToday ? now.hour : date < now.date ? Number.MAX_SAFE_INTEGER : -1;

  const durationOptions = Array.from({ length: maxH - minH + 1 }, (_, i) => {
    const h = i + minH;
    return { value: String(h), label: `${h} hr${h > 1 ? 's' : ''}` };
  });

  const timeSlots = generateTimeSlots(hours, occupiedSlots, selectedTime, currentHour, openHour, closeHour);
  const selectedSlot = selectedTime ? timeSlots.find((slot) => slot.time === selectedTime) : undefined;

  // ─── Effects ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchAvailability = async () => {
      setLoadingSlots(true);
      setSelectedTime('');
      setAvailability(null);
      try {
        const avail = await amenityRepo.getAvailability(amenity.id, date);
        if (cancelled) return;
        setAvailability(avail);
        if (!avail.isClosed && !avail.isBlackout && avail.openTime && avail.closeTime) {
          const openH = parseInt(avail.openTime.split(':')[0]);
          const closeH = parseInt(avail.closeTime.split(':')[0]);
          const fullyOccupied: string[] = [];
          for (let h = openH; h < closeH; h++) {
            const isReserved = avail.reservedSlots.some((s) => {
              const sH = parseInt(s.startTime.split(':')[0]);
              return h >= sH && h < sH + s.hours;
            });
            if (isReserved) fullyOccupied.push(padHour(h));
          }
          setOccupiedSlots(fullyOccupied);
        } else {
          setOccupiedSlots([]);
        }
      } catch {
        if (!cancelled) setOccupiedSlots([]);
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    };
    fetchAvailability();
    return () => { cancelled = true; };
  }, [date, amenity.id, amenityRepo]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleHoursChange = useCallback((val: string) => {
    setHours(parseInt(val));
    setSelectedTime('');
  }, []);

  const handleNext = () => {
    if (step === 1 && (availability?.isClosed || availability?.isBlackout)) {
      toast.warning(
        availability?.isBlackout
          ? t('amenities.modal.blackoutDate')
          : t('amenities.modal.closedDay')
      );
      return;
    }
    if (step === 1 && !selectedTime) {
      toast.warning(t('amenities.wizard.selectTime'));
      return;
    }
    if (step === 1 && (!selectedSlot || !selectedSlot.isValidStart || selectedSlot.isOccupied || selectedSlot.isPast)) {
      toast.warning(t('amenities.errors.slotTaken'));
      setSelectedTime('');
      return;
    }
    if (step === 2 && !acceptedTerms) {
      toast.warning(t('amenities.wizard.acceptTerms'));
      return;
    }
    setStep((s) => s + 1);
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onConfirm({
        amenityId: amenity.id,
        date,
        startTime: selectedTime,
        hours,
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Slot styling ────────────────────────────────────────────────────────
  const getSlotStyle = (slot: TimeSlot) => {
    if (slot.inSelection)
      return 'bg-indigo-600 text-white border-indigo-600 ring-2 ring-indigo-100 shadow scale-105 z-10';
    if (slot.isPast)
      return 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed line-through';
    if (slot.isOccupied)
      return 'bg-slate-100 text-slate-300 border-transparent cursor-not-allowed';
    if (!slot.isValidStart)
      return 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed opacity-50';
    return 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 hover:scale-105 cursor-pointer';
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4 shrink-0">
          <div className="space-y-1">
            <h3 className="font-bold text-slate-900 text-lg leading-tight">
              {amenity.name}
            </h3>
            <p className="text-xs text-slate-400">{t('amenities.wizard.title')}</p>
          </div>
          <div className="flex items-center gap-4">
            <StepIndicator step={step} />
            <button
              onClick={onClose}
              className="text-slate-300 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-100 p-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Progress Bar ────────────────────────────────────────────────── */}
        <div className="h-0.5 w-full bg-slate-100 shrink-0">
          <div
            className="h-0.5 bg-indigo-500 transition-all duration-500 ease-out"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">

          {/* ─── Step 1: Date & Time ─── */}
          {step === 1 && (
            <div className="space-y-5 animate-in slide-in-from-right-4 duration-300">
              <div className="grid grid-cols-2 gap-3">
                <DatePicker
                  label={t('amenities.modal.date')}
                  value={date}
                  min={today}
                  onChange={setDate}
                  required
                />
                <Select
                  label={t('amenities.modal.hours')}
                  value={String(hours)}
                  options={durationOptions}
                  onChange={handleHoursChange}
                />
              </div>

              {/* Booking range pill */}
              {selectedTime && (
                <div className="flex items-center justify-center gap-2 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 animate-in fade-in duration-200">
                  <Clock size={14} className="text-indigo-500 shrink-0" />
                  <span className="text-sm font-semibold text-indigo-700">
                    {selectedTime} – {endTime}
                  </span>
                  <span className="text-xs text-indigo-400 font-medium">
                    ({hours} {t('amenities.modal.hoursLabel')})
                  </span>
                </div>
              )}

              {/* Closed / blackout banner */}
              {!loadingSlots && availability?.isClosed && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
                  <AlertCircle size={15} className="shrink-0" />
                  {availability.isBlackout
                    ? t('amenities.modal.blackoutDate')
                    : t('amenities.modal.closedDay')}
                </div>
              )}

              {/* Time grid */}
              {!availability?.isClosed && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  {t('amenities.modal.selectStartTime')}
                </p>

                {loadingSlots ? (
                  <div className="py-10 text-center text-slate-400 text-sm animate-pulse">
                    {t('amenities.modal.loadingAvailability')}
                  </div>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot.time}
                        disabled={!slot.isValidStart || slot.isOccupied || slot.isPast}
                        onClick={() => setSelectedTime(slot.time)}
                        className={`relative py-2.5 rounded-lg text-xs font-medium border transition-all duration-150 ${getSlotStyle(slot)}`}
                      >
                        {slot.display}
                      </button>
                    ))}
                  </div>
                )}

                {/* Legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 text-[10px] text-slate-400 justify-center">
                  {[
                    { color: 'bg-indigo-600', label: t('amenities.legend.selected') },
                    { color: 'bg-white border border-slate-300', label: t('amenities.legend.available') },
                    { color: 'bg-slate-100', label: t('amenities.legend.occupied') },
                    ...(isToday ? [{ color: 'bg-slate-50 border border-slate-100', label: t('amenities.legend.past') }] : []),
                  ].map(({ color, label }) => (
                    <span key={label} className="flex items-center gap-1">
                      <span className={`w-2.5 h-2.5 rounded ${color} inline-block`} />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
              )}
            </div>
          )}

          {/* ─── Step 2: Rules & Terms ─── */}
          {step === 2 && (
            <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
              <Section icon={<FileText size={14} />} title={t('amenities.contract.rules')}>
                <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">
                  {amenity.rules || t('amenities.noRules')}
                </p>
              </Section>

              <Section icon={<AlertCircle size={14} />} title={t('amenities.contract.terms')}>
                <div className="max-h-32 overflow-y-auto rounded-lg bg-slate-50 border border-slate-100 p-3">
                  <p className="text-xs text-slate-500 whitespace-pre-line leading-relaxed">
                    {amenity.termsAndConditions || t('amenities.genericTerms')}
                  </p>
                </div>
              </Section>

              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3">
                <Checkbox
                  checked={acceptedTerms}
                  onChange={setAcceptedTerms}
                  label={t('amenities.contract.accept')}
                />
              </div>
            </div>
          )}

          {/* ─── Step 3: Confirmation ─── */}
          {step === 3 && (
            <div className="space-y-5 animate-in slide-in-from-right-4 duration-300">
              {/* Hero confirmation */}
              <div className="text-center pt-2 pb-1">
                <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-emerald-500 shadow-sm">
                  <CheckCircle size={32} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">
                  {adminMode ? 'Confirmar reservación' : t('amenities.modal.confirm')}
                </h3>

                {/* Date & time chips */}
                <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                  <Chip icon={<Calendar size={12} />} label={date} />
                  <Chip icon={<Timer size={12} />} label={`${selectedTime} – ${endTime}`} />
                </div>
              </div>

              {/* Cost breakdown */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                  {t('amenities.modal.summary')}
                </p>
                <SummaryRow
                  label={<>{t('amenities.modal.rental')} <span className="text-slate-400">({pricing.pricingMode === AmenityPricingMode.FLAT ? 'total por reserva' : `${hours}h × $${pricing.price}`})</span></>}
                  value={`$${rentalCost}`}
                />
                {depositAmount > 0 && (
                  <SummaryRow
                    label={<span className="text-amber-600">{t('amenities.modal.deposit')}</span>}
                    value={<span className="text-amber-600">${depositAmount}</span>}
                  />
                )}
                <SummaryRow label={t('amenities.modal.total')} value={`$${totalCost}`} highlight />
              </div>

              {adminMode ? (
                <p className="text-xs text-center text-slate-400">
                  La reservación se creará confirmada y se generará el cargo al titular de la unidad.
                </p>
              ) : amenity.requiresApproval ? (
                <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>
                    Esta amenidad <strong>requiere aprobación</strong>. Tu reservación quedará en estado <strong>Pendiente</strong> hasta que el administrador la confirme.
                  </span>
                </div>
              ) : (
                <p className="text-xs text-center text-slate-400">
                  {t('amenities.payment.confirm_msg')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 shrink-0 bg-white">
          {step === 1 ? (
            <Button variant="secondary" onClick={onClose} className="flex-1">
              {t('common.cancel')}
            </Button>
          ) : (
            <Button variant="secondary" onClick={handleBack} className="flex-1" icon={<ChevronLeft size={15} />}>
              {t('amenities.wizard.back')}
            </Button>
          )}

          {step < TOTAL_STEPS ? (
            <Button
              variant="primary"
              onClick={handleNext}
              className="flex-1"
              disabled={step === 1 && !selectedTime}
              icon={<ChevronRight size={15} />}
            >
              {t('amenities.wizard.next')}
            </Button>
          ) : (
            <Button variant="primary" onClick={handleSubmit} className="flex-1" isLoading={loading}>
              {adminMode ? 'Crear reservación' : t('amenities.modal.confirm')}
            </Button>
          )}
        </div>

      </div>
    </div>
  );
};

// ─── Tiny shared primitives ───────────────────────────────────────────────────

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
    <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
      <span className="text-slate-400">{icon}</span>
      {title}
    </h4>
    {children}
  </div>
);

const Chip: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 text-xs font-medium rounded-full px-3 py-1">
    {icon}
    {label}
  </span>
);
